<script setup lang="ts">
import { useQueryClient } from '@tanstack/vue-query';

import { getApiGetPaymentMethodsQueryKey, type PaymentMethodEntity, useApiDetachPaymentMethod } from '~/apiClient';

// PROPS
const { paymentMethod } = defineProps({
    paymentMethod: { type: Object as PropType<PaymentMethodEntity>, required: true },
});

// MODEL
const open = defineModel<boolean>('open', { required: true });

// COMPOSABLES
const { isMobile } = useResponsive();
const { tt } = useTypedI18n();
const { mutateAsync: mutateDetach, isPending: detachIsPending } = useApiDetachPaymentMethod();
const queryClient = useQueryClient();
const { notifyBackendError, notifyGenericError } = useNotification();
const { paymentMethodLabel } = usePaymentMethodInfo(() => paymentMethod);

// METHODS
const closeModal = (): void => {
    open.value = false;
};

const onConfirm = (): void => {
    mutateDetach({ paymentMethodId: paymentMethod.id })
        .then(() => {
            queryClient.refetchQueries({ queryKey: getApiGetPaymentMethodsQueryKey(), type: 'active' });
            closeModal();
        })
        .catch(error => {
            if (isBackendError(error.data)) {
                notifyBackendError({ error: error.data.error });
            } else {
                notifyGenericError();
            }
        });
};
</script>

<template>
    <UModal v-model:open="open" :fullscreen="isMobile" :title="tt('paymentMethods.deleteModal.title')" v-bind="modalNoDescAriaFixupProps">
        <template #body>
            <div class="space-y-6 text-center">
                <p class="text-primary text-lg font-semibold uppercase">{{ paymentMethodLabel }}</p>
                <p>{{ tt('paymentMethods.deleteModal.subtitle') }}</p>
            </div>
        </template>
        <template #footer="{ close }">
            <UButton
                color="error"
                block
                size="xl"
                :label="tt('paymentMethods.deleteModal.confirmButton')"
                :loading="detachIsPending"
                @click="onConfirm"
            />
            <UButton color="neutral" block size="xl" variant="link" :label="tt('paymentMethods.deleteModal.cancelButton')" @click="close" />
        </template>
    </UModal>
</template>
